import React, { useEffect, useState, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, Link as RouterLink } from 'react-router-dom';
import RichTextEditor from 'react-rte';
import { DateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Alert from '@material-ui/lab/Alert';

// Local imports
import { paper } from 'app/styles/core';
import useApi from 'app/context/useApi';
import { containerFlex, containerFlexFixed, containerFlexGrow } from 'app/styles/new-layout';
import Loading from 'app/components/loaders/Loading';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        className={props.className}
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingRight: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    editor: {
        marginTop: theme.spacing(3),
        '& .public-DraftEditor-content': {
            minHeight: 400,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
        }
    },
    buttonGroup: {
        width: '100%',
    },
    buttonExpand: {
        flexGrow: 1
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginRight: {
        marginRight: theme.spacing(3),
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    alert: {
        marginBottom: theme.spacing(3),
        textTransform: 'capitalize'
    }
}));

const initialArticle = {
    id: 0,
    group_id: 0,
    title: "",
    message: "",
    commentable: false,
    published_at: null
}

const publishOptions = {
    new: ['Publish Now', 'Publish Later', 'Save as Draft'],
    draft: ['Update Draft', 'Publish Now', 'Publish Later'],
    scheduled: ['Update Draft', 'Publish Now'],
    published: ['Update Article', 'Save as Draft'],
}

export default function EditArticle() {

    // State and constants
    const classes = useStyles();
    const api = useApi();
    const params = useParams();
    const history = useHistory();
    const anchorRef = useRef(null);

    const [ article, setArticle ] = useState(initialArticle);
    const [ groups, setGroups ] = useState([]);
    const [ status, setStatus ] = useState('new');

    const [ editorState, setEditorState] = useState(RichTextEditor.createEmptyValue());

    const [ publishButton, setPublishButton ] = useState(publishOptions.new[0]);
    const [ publishOptionsOpen, setPublishOptionsOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    // Getter function
    const getArticle = () => {

        setLoading(true);

        api.get(`/admin/article/${params.id}`)
            .then(res => {
                setArticle(res.article);
                if (res.article.message) {
                    setEditorState(RichTextEditor.createValueFromString(res.article.message, 'html'));
                };
                setStatus(res.article.status);
            }).finally(() => setLoading(false))
    };

    const getGroups = () => api.get('/admin/group').then(res => setGroups(res.groups));

    useEffect(() => {
        if (params.id > 0) getArticle();
        getGroups();
    }, []);

    useEffect(() => {
        setPublishButton(publishOptions[status][0]);
    }, [status]);

    // Handlers

    const handleChange = name => event => {
        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (isError(name)) delete errors[name];

        setArticle({ ...article, [name]: value });
    }

    const handleToggle = () => {
        setPublishOptionsOpen(!publishOptionsOpen);
    }

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setPublishOptionsOpen(false);
    };

    const handlePublishOption = option => {
        setPublishButton(option);
        setPublishOptionsOpen(false);
    }

    // Posters

    const postArticle = () => {

        setLoading(true);

        const data = {
            ...article,
            publish: publishButton
        }

        data.message = editorState.toString('html');

        if (DateTime.isDateTime(article.published_at)) {
            data.published_at = article.published_at.toSeconds();
        }

        api.post(`/admin/article/${params.id}`, data)
            .then(() => history.push('/admin/articles'))
            .catch((error) => {
                setLoading(false);
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            })
    };

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const publishText = useMemo(() => {

        if (article.published_at && status === 'published') {
            return 'Published On: ' + DateTime.fromISO(article.published_at).toLocaleString(DateTime.DATETIME_MED);
        }
        if (article.published_at && status === 'scheduled') {
            return 'Scheduled For: ' + DateTime.fromISO(article.published_at).toLocaleString(DateTime.DATETIME_MED);
        }

        return null;
    }, [status])

    const severity = useMemo(() => {
        switch (status) {
            case 'published': return 'success';
            case 'scheduled': return 'warning';
            case 'draft':
            default: return 'info'
        }
    }, [status])

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Paper className={classes.paper}>
                    <Loading isLoading={loading}/>
                    <Typography variant="h6">
                        {article.title.length ? article.title : "New Article"}
                    </Typography>
                    <TextField
                        variant='outlined'
                        label='Title'
                        value={article.title}
                        id="input-title"
                        onChange={handleChange('title')}
                        fullWidth
                        margin="normal"
                        error={isError('title')}
                        helperText={getErrorMessage('title')}
                        className={classes.marginTop}
                    />
                    <RichTextEditor
                        className={classes.editor}
                        value={editorState}
                        onChange={value => setEditorState(value)}
                    />
                </Paper>
            </div>
            <div className={classes.nav}>
                <Paper className={classes.paper}>
                    <Typography variant="h6" className={classes.marginBottom}>
                        Article Options
                    </Typography>
                    {(groups.length) &&
                        <>
                            <Typography variant='subtitle2' gutterBottom>
                                Select the group of users that can view this article.
                            </Typography>
                            <TextField
                                id="group-select"
                                select
                                value={article.group_id}
                                onChange={handleChange('group_id')}
                                helperText={getErrorMessage('group_id')}
                                variant="outlined"
                                fullWidth
                                color="primary"
                                >
                                <MenuItem key={0} value={0}>
                                    All Users
                                </MenuItem>
                                {groups.map(group => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {group.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    }
                    <Typography variant='subtitle2' className={classes.marginTop}>
                        Should users be able to comment on this article?
                    </Typography>
                    <FormGroup row margin="normal">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={article.commentable}
                                    onChange={handleChange('commentable')}
                                    name="commentable"
                                    color="primary"
                                />
                            }
                            label="Turn on comments"
                        />
                    </FormGroup>
                </Paper>
                <Paper className={classes.paper}>
                    {(article.id > 0 && article.updated_at) &&
                        <>
                            <Alert className={classes.alert} severity={severity}>
                                {status} Article
                            </Alert>
                            <Typography
                                color="primary"
                                variant="subtitle2"
                                className={classes.marginBottom}
                                >
                                <span>
                                    {'Last Updated: ' + DateTime.fromISO(article.updated_at).toLocaleString(DateTime.DATETIME_MED)}
                                </span>
                                <br/>
                                <span>
                                    {publishText}
                                </span>
                            </Typography>
                        </>
                    }
                    <ButtonGroup
                        variant="contained"
                        color="primary"
                        ref={anchorRef}
                        aria-label="Publish Article"
                        className={classes.buttonGroup}
                        disabled={loading}
                        >
                        <Button className={classes.buttonExpand} onClick={postArticle}>
                            {publishButton}
                        </Button>
                        <Button
                            color="primary"
                            size="small"
                            aria-controls={publishOptionsOpen ? 'publish-options-menu' : undefined}
                            aria-expanded={publishOptionsOpen ? 'true' : undefined}
                            aria-label="Select Publish Option"
                            aria-haspopup="menu"
                            onClick={handleToggle}
                            >
                            <ArrowDropDownIcon />
                        </Button>
                    </ButtonGroup>
                    <Popper
                        open={publishOptionsOpen}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        transition
                        placement="bottom-end"
                        >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'center top',
                                }}
                                >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList id="publish-options-menu">
                                            {publishOptions[status].map((option, index) => (
                                                <MenuItem
                                                    key={index}
                                                    selected={option === publishButton}
                                                    onClick={() => handlePublishOption(option)}
                                                    >
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                    <Collapse in={publishButton === 'Publish Later' || status === 'scheduled'}>
                        <DateTimePicker
                            label="Publish Date"
                            inputVariant="outlined"
                            value={article.published_at}
                            onChange={handleChange('published_at')}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={5}
                            className={classes.marginTop}
                            disablePast
                            clearable
                        />
                    </Collapse>
                    <Button
                        className={classes.marginTop}
                        variant="outlined"
                        fullWidth
                        component={RouterLink}
                        to="/admin/articles"
                        >
                        Back to Articles
                    </Button>
                </Paper>
            </div>
        </div>
    )
}
